import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { Input, Radios } from '@feathr/components';

interface ILinksTabProps {
  creative: DisplayCreative;
}

function LinksTab({ creative }: Readonly<ILinksTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const useOriginalDestination = !creative.get('destination_url');
  const [destinationMode, setDestinationMode] = useState<string>(
    useOriginalDestination ? 'default' : 'custom',
  );

  const hasAltText = !!creative.get('alt_text');
  const [altTextMode, setAltTextMode] = useState<string>(hasAltText ? 'custom' : 'default');

  function handleDestinationModeChange(value?: string): void {
    if (value) {
      setDestinationMode(value);
      if (value === 'default') {
        creative.set({ destination_url: undefined });
      }
    }
  }

  function handleAltTextModeChange(value?: string): void {
    if (value) {
      setAltTextMode(value);
      if (value === 'default') {
        creative.set({ alt_text: undefined });
      }
    }
  }

  const destinationOptions = [
    { id: 'default', name: t('Default URL') },
    { id: 'custom', name: t('Custom URL') },
  ];

  const altTextOptions = [
    { id: 'default', name: t('Default text') },
    { id: 'custom', name: t('Custom text') },
  ];

  return (
    <Stack gap={theme.spacing['4']}>
      <Stack gap={theme.spacing['2']}>
        <Radios
          label={t('Destination URL')}
          layout={'block'}
          name={'destination-url-mode'}
          onChange={handleDestinationModeChange}
          options={destinationOptions}
          orientation={'horizontal'}
          value={destinationMode}
        />
        {destinationMode === 'custom' && (
          <Input
            attribute={'destination_url'}
            model={creative}
            placeholder={'https://example.com'}
            type={'url'}
          />
        )}
      </Stack>

      {creative.channel !== 'meta' && (
        <Stack gap={theme.spacing['2']}>
          <Radios
            helpText={t('Describe the image for people with visual impairments')}
            label={t('Alt text')}
            layout={'block'}
            name={'alt-text-mode'}
            onChange={handleAltTextModeChange}
            options={altTextOptions}
            orientation={'horizontal'}
            value={altTextMode}
          />
          {altTextMode === 'custom' && (
            <Input attribute={'alt_text'} model={creative} type={'text'} />
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default observer(LinksTab);
